// Bilingual content dictionary for Magis Belgium — based on the corporate presentation
export const CONTENT = {
  en: {
    nav: {
      links: [
        { id: "mission", label: "Mission" },
        { id: "expertise", label: "Services" },
        { id: "operating", label: "Approach" },
        { id: "proven", label: "Track record" },
        { id: "group", label: "Group" },
        { id: "contact", label: "Contact" },
      ],
      cta: "Set the course",
    },
    hero: {
      eyebrow: "Magis Belgium · Strategy & General Secretariat",
      titleLines: ["We help international", "associations realise", "their destination"],
      subtitle:
        "An independent Brussels-based partner for the associations shaping Europe. We align around the strategy, manage the secretariat, and drive progress — with the rigour EU decision-making requires.",
      ctaPrimary: "Set the course together",
      ctaSecondary: "Our expertise",
      groupLine: "Part of Magis Company · Brussels · Turin · São Paulo",
    },
    marquee: ["Strategy", "General Secretariat", "Governance", "AISBL Compliance", "EU Institutions", "EPSG", "Membership", "Continuity", "Brussels"],
    mission: {
      eyebrow: "Our mission",
      title: "Why we exist",
      lead: "We exist to help international organisations save time, reduce complexity and unlock opportunities.",
      body: "Every association has a purpose. Our role is to help bring that purpose to life. We align around the strategy, manage the secretariat, and drive progress.",
    },
    whatWeDo: {
      eyebrow: "What we do",
      title: "An independent partner for the associations shaping Europe",
      items: [
        {
          title: "Focus on what matters",
          body: "Save executive time and reduce operational complexity so your leadership can concentrate on strategy.",
        },
        {
          title: "Strengthen the organisation",
          body: "Enhance governance and access specialised expertise precisely when it is needed.",
        },
        {
          title: "Enable sustainable growth",
          body: "Identify new opportunities, build stronger stakeholder relationships, and navigate the Belgian and European environment with confidence.",
        },
      ],
    },
    expertise: {
      eyebrow: "Our areas of expertise",
      title: "Three pillars. One mission: advancing your association",
      pillars: [
        {
          n: "01",
          title: "Strategy",
          items: [
            "Positioning, growth and membership strategy",
            "Market and regulatory intelligence",
            "Business models and partnership development",
          ],
        },
        {
          n: "02",
          title: "General Secretariat",
          items: [
            "Board effectiveness and governance design",
            "Agenda, plenary and working-group management",
            "Stakeholder coordination, representation, consultations and reporting",
          ],
        },
        {
          n: "03",
          title: "Operations, Compliance & Continuity",
          items: [
            "Belgian AISBL compliance management",
            "Accounting coordination, budgeting and administrative support",
            "Daily management, documented processes and institutional continuity",
          ],
        },
      ],
    },
    operating: {
      eyebrow: "Our operating model",
      title: "A Brussels-based consultancy built on a reliable operational model",
      items: [
        {
          title: "Immediate expertise",
          body: "Experienced professionals, ready to operate, with the right expertise when you need it.",
        },
        {
          title: "Flexible & predictable model",
          body: "A multidisciplinary team without employment or administrative burden, supported by a clear monthly model.",
        },
        {
          title: "Continuity you can rely on",
          body: "Documented processes, backup resources, and institutional knowledge preserved over time.",
        },
      ],
    },
    proven: {
      eyebrow: "Proven where it counts",
      title: "Credibility earned inside Europe's institutions",
      lead: "Our method was forged in one of the most demanding arenas in Brussels — EU payments. We start with value. We bring an ecosystem. We grow with our clients.",
      items: [
        {
          title: "General Secretariat to EPSG",
          body: "Acting secretariat to the European Payments Stakeholder Group — the European Commission's advisory forum on retail payments.",
        },
        {
          title: "Multi-stakeholder network",
          body: "Trusted relationships with the Commission, ECB, EBA, EPC, national regulators and leading sector operators.",
        },
        {
          title: "Institutional-grade delivery",
          body: "Position papers, consultation responses and briefing notes held to the standard EU decision-making requires.",
        },
      ],
      stats: [
        { value: "15+", label: "years of experience in strategic advisory services" },
        { value: "8+", label: "years advising EU institutions & active EPSG secretariat mandate" },
        { value: "3", label: "international hubs across Europe & Latin America" },
      ],
    },
    ecosystem: {
      eyebrow: "Our ecosystem",
      title: "Part of an international ecosystem",
      body: "Magis Belgium is connected to PWT — Performance With Trust — an integrated network of boutique firms. One relationship gives our clients a full bench of specialists, mobilised only when they add value.",
      partnersLabel: "Beyond strategy & secretariat, clients can access our partners in:",
      partners: ["Legal & fiscal", "Operations", "Venture building", "Compliance", "Intellectual property", "Digital transformation (AI)"],
      link: "www.pwtsystem.com",
    },
    group: {
      eyebrow: "Our group",
      title: "Part of Magis Company",
      lead: "An international management-consulting group. One method, three hubs, a shared commitment to trust and performance.",
      hubs: [
        {
          status: "Live",
          city: "Brussels",
          country: "Belgium",
          body: "General Secretariat & strategy for international associations and EU institutions.",
        },
        {
          status: "Live",
          city: "Turin",
          country: "Italy",
          body: "Full-service management consulting — strategy, operations and organisational transformation.",
        },
        {
          status: "Opening soon",
          city: "São Paulo",
          country: "Brazil",
          body: "Forthcoming hub serving Brazilian and Latin American clients.",
        },
      ],
    },
    values: {
      eyebrow: "What guides us",
      title: "The values that set our compass",
      items: [
        { n: "01", title: "Integrity", body: "We provide candid counsel and realistic assessments, grounded in facts rather than expectations. Meaningful progress requires clarity and honesty." },
        { n: "02", title: "Mutual Trust", body: "Partnerships that endure. We build confidence through consistency and foster relationships based on mutual trust." },
        { n: "03", title: "Tenacity", body: "Commitment through every stage. We stay focused through challenges and negotiations until results are achieved." },
      ],
    },
    whyMagis: {
      eyebrow: "Why Magis Belgium",
      title: "What sets us apart",
      items: [
        { title: "Strategy and delivery, together", body: "The rare firm that both sets the direction and runs the secretariat that executes it." },
        { title: "Trust, rigour, discretion", body: "Sensitive mandates delivered with confidentiality and a focus on concrete outcomes." },
        { title: "International by design", body: "Brussels roots inside Magis Group's European and Latin American footprint." },
      ],
    },
    vision: {
      eyebrow: "Our vision",
      title: "To become the reference point for international associations",
      body: "The trusted name a board thinks of first when strategy, governance and representation truly matter.",
    },
    contact: {
      eyebrow: "Where we are",
      title: "Let's set the course together",
      body: "Tell us where your association wants to go. We'll help you chart the route — and run the secretariat that keeps you on it.",
      locationLabel: "European Quarter · Brussels",
      form: {
        name: "Full name",
        email: "Email address",
        organisation: "Organisation",
        message: "Where does your association want to go?",
        submit: "Send enquiry",
        sending: "Sending…",
        success: "Thank you — your message has been sent. We'll be in touch shortly.",
        error: "Something went wrong. Please try again or email us directly.",
      },
    },
    footer: {
      tagline: "Strategy & General Secretariat for the international associations shaping Europe.",
      rights: "All rights reserved.",
      addressLabel: "Office",
      contactLabel: "Contact",
      groupLabel: "Group",
      vatLabel: "VAT",
      privacy: "Privacy Policy",
      legal: "Legal Notice",
      backHome: "Back to home",
    },
  },

  fr: {
    nav: {
      links: [
        { id: "mission", label: "Mission" },
        { id: "expertise", label: "Services" },
        { id: "operating", label: "Approche" },
        { id: "proven", label: "Références" },
        { id: "group", label: "Groupe" },
        { id: "contact", label: "Contact" },
      ],
      cta: "Fixer le cap",
    },
    hero: {
      eyebrow: "Magis Belgium · Stratégie & Secrétariat général",
      titleLines: ["Nous aidons les associations", "internationales à atteindre", "leur destination"],
      subtitle:
        "Un partenaire indépendant basé à Bruxelles pour les associations qui façonnent l'Europe. Nous nous alignons sur la stratégie, gérons le secrétariat et faisons avancer les projets — avec la rigueur qu'exige la décision européenne.",
      ctaPrimary: "Fixons le cap ensemble",
      ctaSecondary: "Notre expertise",
      groupLine: "Membre de Magis Company · Bruxelles · Turin · São Paulo",
    },
    marquee: ["Stratégie", "Secrétariat général", "Gouvernance", "Conformité AISBL", "Institutions UE", "EPSG", "Membres", "Continuité", "Bruxelles"],
    mission: {
      eyebrow: "Notre mission",
      title: "Pourquoi nous existons",
      lead: "Nous existons pour aider les organisations internationales à gagner du temps, réduire la complexité et saisir les opportunités.",
      body: "Chaque association a une raison d'être. Notre rôle est de l'aider à la concrétiser. Nous nous alignons sur la stratégie, gérons le secrétariat et faisons avancer les projets.",
    },
    whatWeDo: {
      eyebrow: "Ce que nous faisons",
      title: "Un partenaire indépendant pour les associations qui façonnent l'Europe",
      items: [
        {
          title: "Se concentrer sur l'essentiel",
          body: "Faire gagner du temps aux dirigeants et réduire la complexité opérationnelle pour que la direction se concentre sur la stratégie.",
        },
        {
          title: "Renforcer l'organisation",
          body: "Améliorer la gouvernance et accéder à une expertise spécialisée précisément quand elle est nécessaire.",
        },
        {
          title: "Permettre une croissance durable",
          body: "Identifier de nouvelles opportunités, renforcer les relations avec les parties prenantes et naviguer avec confiance dans l'environnement belge et européen.",
        },
      ],
    },
    expertise: {
      eyebrow: "Nos domaines d'expertise",
      title: "Trois piliers. Une mission : faire progresser votre association",
      pillars: [
        {
          n: "01",
          title: "Stratégie",
          items: [
            "Positionnement, croissance et stratégie d'adhésion",
            "Veille de marché et réglementaire",
            "Modèles économiques et développement de partenariats",
          ],
        },
        {
          n: "02",
          title: "Secrétariat général",
          items: [
            "Efficacité du conseil et conception de la gouvernance",
            "Gestion des ordres du jour, des plénières et des groupes de travail",
            "Coordination des parties prenantes, représentation, consultations et reporting",
          ],
        },
        {
          n: "03",
          title: "Opérations, conformité & continuité",
          items: [
            "Gestion de la conformité AISBL belge",
            "Coordination comptable, budgétisation et support administratif",
            "Gestion quotidienne, processus documentés et continuité institutionnelle",
          ],
        },
      ],
    },
    operating: {
      eyebrow: "Notre modèle opérationnel",
      title: "Un cabinet basé à Bruxelles bâti sur un modèle opérationnel fiable",
      items: [
        {
          title: "Expertise immédiate",
          body: "Des professionnels expérimentés, opérationnels, avec l'expertise adaptée au moment où vous en avez besoin.",
        },
        {
          title: "Modèle flexible & prévisible",
          body: "Une équipe pluridisciplinaire sans charge d'emploi ni administrative, portée par un modèle mensuel clair.",
        },
        {
          title: "Une continuité fiable",
          body: "Processus documentés, ressources de secours et savoir institutionnel préservé dans le temps.",
        },
      ],
    },
    proven: {
      eyebrow: "Éprouvé là où ça compte",
      title: "Une crédibilité forgée au cœur des institutions européennes",
      lead: "Notre méthode s'est forgée dans l'une des arènes les plus exigeantes de Bruxelles — les paiements européens. Nous commençons par la valeur. Nous apportons un écosystème. Nous grandissons avec nos clients.",
      items: [
        {
          title: "Secrétariat général de l'EPSG",
          body: "Secrétariat en exercice de l'European Payments Stakeholder Group — le forum consultatif de la Commission européenne sur les paiements de détail.",
        },
        {
          title: "Réseau multi-parties prenantes",
          body: "Des relations de confiance avec la Commission, la BCE, l'EBA, l'EPC, les régulateurs nationaux et les principaux acteurs du secteur.",
        },
        {
          title: "Une exécution de niveau institutionnel",
          body: "Prises de position, réponses aux consultations et notes d'information au standard qu'exige la décision européenne.",
        },
      ],
      stats: [
        { value: "15+", label: "ans d'expérience en conseil stratégique" },
        { value: "8+", label: "ans de conseil aux institutions UE & mandat de secrétariat EPSG actif" },
        { value: "3", label: "hubs internationaux en Europe & Amérique latine" },
      ],
    },
    ecosystem: {
      eyebrow: "Notre écosystème",
      title: "Membre d'un écosystème international",
      body: "Magis Belgium est connectée à PWT — Performance With Trust — un réseau intégré de cabinets boutique. Une seule relation donne à nos clients accès à un banc complet de spécialistes, mobilisés uniquement lorsqu'ils apportent de la valeur.",
      partnersLabel: "Au-delà de la stratégie et du secrétariat, nos clients accèdent à nos partenaires en :",
      partners: ["Juridique & fiscal", "Opérations", "Venture building", "Conformité", "Propriété intellectuelle", "Transformation digitale (IA)"],
      link: "www.pwtsystem.com",
    },
    group: {
      eyebrow: "Notre groupe",
      title: "Membre de Magis Company",
      lead: "Un groupe international de conseil en management. Une méthode, trois hubs, un engagement partagé envers la confiance et la performance.",
      hubs: [
        {
          status: "Actif",
          city: "Bruxelles",
          country: "Belgique",
          body: "Secrétariat général & stratégie pour les associations internationales et les institutions européennes.",
        },
        {
          status: "Actif",
          city: "Turin",
          country: "Italie",
          body: "Conseil en management complet — stratégie, opérations et transformation organisationnelle.",
        },
        {
          status: "Bientôt",
          city: "São Paulo",
          country: "Brésil",
          body: "Futur hub au service des clients brésiliens et latino-américains.",
        },
      ],
    },
    values: {
      eyebrow: "Ce qui nous guide",
      title: "Les valeurs qui fixent notre cap",
      items: [
        { n: "01", title: "Intégrité", body: "Nous offrons un conseil franc et des évaluations réalistes, fondés sur les faits plutôt que sur les attentes. Le progrès véritable exige clarté et honnêteté." },
        { n: "02", title: "Confiance mutuelle", body: "Des partenariats qui durent. Nous bâtissons la confiance par la constance et cultivons des relations fondées sur la réciprocité." },
        { n: "03", title: "Ténacité", body: "L'engagement à chaque étape. Nous restons concentrés à travers les défis et les négociations jusqu'à l'obtention de résultats." },
      ],
    },
    whyMagis: {
      eyebrow: "Pourquoi Magis Belgium",
      title: "Ce qui nous distingue",
      items: [
        { title: "Stratégie et exécution, ensemble", body: "Le rare cabinet qui fixe le cap et gère le secrétariat qui l'exécute." },
        { title: "Confiance, rigueur, discrétion", body: "Des mandats sensibles menés avec confidentialité et un souci de résultats concrets." },
        { title: "International par nature", body: "Des racines bruxelloises au sein de l'empreinte européenne et latino-américaine de Magis Group." },
      ],
    },
    vision: {
      eyebrow: "Notre vision",
      title: "Devenir la référence des associations internationales",
      body: "Le nom de confiance auquel un conseil pense en premier lorsque la stratégie, la gouvernance et la représentation comptent vraiment.",
    },
    contact: {
      eyebrow: "Où nous trouver",
      title: "Fixons le cap ensemble",
      body: "Dites-nous où votre association souhaite aller. Nous vous aiderons à tracer la route — et à gérer le secrétariat qui vous y maintient.",
      locationLabel: "Quartier européen · Bruxelles",
      form: {
        name: "Nom complet",
        email: "Adresse e-mail",
        organisation: "Organisation",
        message: "Où votre association souhaite-t-elle aller ?",
        submit: "Envoyer",
        sending: "Envoi…",
        success: "Merci — votre message a été envoyé. Nous vous recontacterons rapidement.",
        error: "Une erreur est survenue. Réessayez ou écrivez-nous directement.",
      },
    },
    footer: {
      tagline: "Stratégie & Secrétariat général pour les associations internationales qui façonnent l'Europe.",
      rights: "Tous droits réservés.",
      addressLabel: "Bureau",
      contactLabel: "Contact",
      groupLabel: "Groupe",
      vatLabel: "TVA",
      privacy: "Politique de confidentialité",
      legal: "Mentions légales",
      backHome: "Retour à l'accueil",
    },
  },
};

export const COMPANY = {
  name: "Magis Belgium",
  legal: "Magis Belgium SRL",
  email: "administration@magis-company.be",
  website: "www.magis-company.com",
  address: "Rue d'Arlon 25, 1050 Brussels, Belgium",
  vat: "BE 1031.922.226",
  group: "Part of Magis Company · Brussels · Turin · São Paulo",
  coordinates: "50.84° N · 4.37° E",
};

export const IMAGES = {
  hero: "https://images.pexels.com/photos/6791897/pexels-photo-6791897.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600",
  brussels: "https://images.unsplash.com/photo-1575845664732-ee40fdc525a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  boardroom: "https://images.pexels.com/photos/16859955/pexels-photo-16859955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
  meeting: "https://images.pexels.com/photos/7433919/pexels-photo-7433919.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
  signing: "https://images.pexels.com/photos/7054510/pexels-photo-7054510.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
  euFlags: "https://images.unsplash.com/photo-1594810205183-18a8b0ce6c13?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  handshake: "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  turin: "https://images.unsplash.com/photo-1610651219730-6b580d616e72?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  saopaulo: "https://images.unsplash.com/photo-1572894234976-d961418c709d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};
